import { c as _c } from "react/compiler-runtime";
import { FirstInteractionFilterConfigValues, GetInteractionAggregatesReqFiltersV2Item, QueryType, TimeRangeConfig } from '@/api/generated/model';
import React from 'react';
import { PopperContentProps } from '@radix-ui/react-popper';
import { WithElementId } from '@/types/component';
import { FilterButton } from '@/comps/FilterPicker/FilterButton';
import { useFetchAvailableFilters } from '@/hooks/use-available-tags';
import { IconType } from '@/constants/icons';
export type VolumeKind = 'users' | 'interactions';
export type AvailableValue = {
  value: string;
  volume?: number;
};
export type GenericFilter = {
  column: string;
  values: string[];
  is_exclude?: boolean;
  select_all?: boolean;
  /** Text match for "contains"operator (API-compatible via filter search field) */
  search?: string | null;
};
export type SpecialFilter = {
  column: string;
  values: FirstInteractionFilterConfigValues;
  is_exclude?: boolean;
};
export type GenericUserFilter = {
  column: string;
  values: string[] | FirstInteractionFilterConfigValues;
  is_exclude?: boolean;
};
export type FilterPickerDraftActions = {
  onReset?: () => GenericFilter[] | void;
  /** Optional filters override so callers can save an unapplied draft in the same click. */
  onSaveForEveryone: (filters?: GenericFilter[]) => void;
  canSaveForEveryone?: boolean;
  /** Gate the save on at least one defined (create-project); reports save timerange-only drafts too. */
  requireDefinedFilters?: boolean;
  isSaving?: boolean;
  saveDisabledMessage?: string;
  /** Shown instead of`saveDisabledMessage` when the empty draft is the only thing blocking the save. */
  noDefinedFiltersMessage?: string;
  /** Defaults to true. When false the draft is handed over unapplied and the picker stays open. */
  appliesDraftOnSave?: boolean;
  saveLabel?: string;
  saveTooltip?: string;
};
export type FilterButtonProps<T extends GenericFilter | GenericUserFilter> = React.PropsWithChildren<WithElementId & {
  filters: T[];
  setFilters: (v: T[]) => void;
  filters_v2?: GetInteractionAggregatesReqFiltersV2Item[];
  additionalFilters?: T[];
  icon?: IconType;
  timeRange: TimeRangeConfig | undefined;
  projectId: string;
  availableFilterKinds: string[];
  volumeKind: VolumeKind | undefined;
  className?: string;
  disabled?: boolean;
  disabledMessage?: string;
  showFilters?: boolean;
  showFilterBy?: boolean;
  filterListPrepend?: React.ReactNode;
  showDraftActions?: boolean;
  draftActions?: FilterPickerDraftActions;
  /** Purple-dot indicator on the filter trigger (defaults to showDraftActions). */
  hasDraftEdits?: boolean;
  ignoreFilters?: T[];
  title?: string;
  align?: 'left' | 'right';
  onChecked?: (checked: boolean, filterKind: string | null, filters: T[]) => void;
  showAdditionalFilterKinds?: boolean;
  // use this only for appearance reasons,
  // e.g. on Retention page filters,
  // we want to show the selected filters,
  // but only UI wise
  overrideSelectedFilters?: T[];
  loading?: boolean;
  side?: PopperContentProps['side'];
  preOpen?: boolean;
  /** Blocks close requests — e.g. while a modal opened from the picker holds focus. */
  keepOpen?: boolean;
  hideSavedFilters?: boolean;
  triggerClassName?: string;
  isGlobal?: boolean;
  /** When true, blocks pointer events on the rest of the page while open. */
  modal?: boolean;
}>;
export type FilterSectionProps<T extends GenericFilter> = WithElementId & {
  filters: T[];
  setFilters: (v: T[]) => void;
  additionalFilters?: T[];
  timeRange: TimeRangeConfig | undefined;
  projectId: string;
  availableFilterKinds: string[];
  volumeKind: VolumeKind | undefined;
  className?: string;
  disabled?: boolean;
  disabledMessage?: string;
  filterListPrepend?: React.ReactNode;
  ignoreFilters?: T[];
  visibleInteractionColumns?: string[];
  type: QueryType;
  hideSavedFilters?: boolean;
  triggerClassName?: string;
  isGlobal?: boolean;
};
export function FilterPickerV2(t0) {
  const $ = _c(33);
  const {
    filters,
    setFilters,
    projectId,
    timeRange,
    additionalFilters,
    volumeKind,
    title,
    filterListPrepend,
    showDraftActions,
    draftActions,
    hasDraftEdits,
    align,
    children,
    disabled,
    disabledMessage,
    showAdditionalFilterKinds: t1,
    ignoreFilters,
    side,
    id,
    type,
    icon,
    hideSavedFilters,
    triggerClassName,
    isGlobal,
    keepOpen,
    modal
  } = t0;
  const showAdditionalFilterKinds = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== filters || $[1] !== projectId || $[2] !== timeRange || $[3] !== type) {
    t2 = {
      projectId,
      filters,
      timeRange,
      type
    };
    $[0] = filters;
    $[1] = projectId;
    $[2] = timeRange;
    $[3] = type;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: filterKinds,
    isLoading: isLoadingFilterKinds
  } = useFetchAvailableFilters(t2);
  let t3;
  if ($[5] !== additionalFilters || $[6] !== align || $[7] !== children || $[8] !== disabled || $[9] !== disabledMessage || $[10] !== draftActions || $[11] !== filterKinds || $[12] !== filterListPrepend || $[13] !== filters || $[14] !== hasDraftEdits || $[15] !== hideSavedFilters || $[16] !== icon || $[17] !== id || $[18] !== ignoreFilters || $[19] !== isGlobal || $[20] !== isLoadingFilterKinds || $[21] !== keepOpen || $[22] !== modal || $[23] !== projectId || $[24] !== setFilters || $[25] !== showAdditionalFilterKinds || $[26] !== showDraftActions || $[27] !== side || $[28] !== timeRange || $[29] !== title || $[30] !== triggerClassName || $[31] !== volumeKind) {
    t3 = <FilterButton id={id} className="z-1000!" filterListPrepend={filterListPrepend} showDraftActions={showDraftActions} draftActions={draftActions} hasDraftEdits={hasDraftEdits} additionalFilters={additionalFilters} availableFilterKinds={filterKinds} filters={filters} setFilters={setFilters} projectId={projectId} timeRange={timeRange} disabled={disabled} disabledMessage={disabledMessage} volumeKind={volumeKind} title={title} align={align} showAdditionalFilterKinds={showAdditionalFilterKinds} loading={isLoadingFilterKinds} ignoreFilters={ignoreFilters} side={side} icon={icon} hideSavedFilters={hideSavedFilters} triggerClassName={triggerClassName} isGlobal={isGlobal} keepOpen={keepOpen} modal={modal}>{children}</FilterButton>;
    $[5] = additionalFilters;
    $[6] = align;
    $[7] = children;
    $[8] = disabled;
    $[9] = disabledMessage;
    $[10] = draftActions;
    $[11] = filterKinds;
    $[12] = filterListPrepend;
    $[13] = filters;
    $[14] = hasDraftEdits;
    $[15] = hideSavedFilters;
    $[16] = icon;
    $[17] = id;
    $[18] = ignoreFilters;
    $[19] = isGlobal;
    $[20] = isLoadingFilterKinds;
    $[21] = keepOpen;
    $[22] = modal;
    $[23] = projectId;
    $[24] = setFilters;
    $[25] = showAdditionalFilterKinds;
    $[26] = showDraftActions;
    $[27] = side;
    $[28] = timeRange;
    $[29] = title;
    $[30] = triggerClassName;
    $[31] = volumeKind;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  return t3;
}

// ===================================================================
// MISC
// ===================================================================